import React, { useEffect, useState, useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Box, Typography, Button, Paper, Stack, IconButton, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import serviceMonitorService from '../../service/serviceMonitor.service';
import serviceCoreService from '../../service/serviceCore.service';
import { ButtonUI, CheckboxUI, DialogUI, FileUploadDialogUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

const ServiceMonitorReportDialog = ({ open, onClose, maxWidth, selectedTaskId, setRefetch, reportId }) => {
  const defaultReportData = {
    serviceTaskId: null,
    taskAttachments: [],
    taskDescription: '',
    checklistItems: [],
    prevTaskAttachments: [],
  };
  const defaultChecklistItem = {
    checklistId: null,
    checklistAttachments: [],
    checklistComment: '',
    name: '',
    checked: false,
    prevAttachments: [],
  };

  const [task, setTask] = useState(null);
  const [reportData, setReportData] = useState(defaultReportData);
  const [attachmentDialogTarget, setAttachmentDialogTarget] = useState(null); // { type: 'task'|'checklist', index? }

  const fetchTaskData = async () => {
    const data = await serviceCoreService.getServiceTask(selectedTaskId);
    if (!data) {
      addNotification({
        content: 'Hiba a feladat adatainak lekérésekor.',
        type: NotificationType.ERROR,
      });
    }
    setTask(data);

    const checklistItems = (data.serviceTaskCheckList || []).map((item) => ({
      ...defaultChecklistItem,
      checklistId: item.id,
      // propagate required flags if present
      attachmentRequired: item.attachRequired,
      descriptionRequired: item.descriptionRequired,
      name: item.name,
      checklistComment: item.description || '',
      checked: item.checked || false,
      prevAttachments: item.serviceTaskAttachment || [],
    }));

    const prevTaskAttachments = data?.serviceTaskAttachment || data?.attachments || [];

    setReportData({
      ...defaultReportData,
      serviceTaskId: selectedTaskId,
      checklistItems,
      // propagate task level requirement flags if present
      taskAttachmentRequired: data.attachRequired,
      taskDescriptionRequired: data.descriptionRequired,
      prevTaskAttachments,
    });
  };

  useEffect(() => {
    if (!selectedTaskId) return;
    fetchTaskData();
  }, [selectedTaskId]);

  const validationSchema = useMemo(() => {
    if (!task) return Yup.object({});
    return Yup.object({
      taskDescription: Yup.string().trim().nullable(),
      taskAttachments: Yup.array(),
      checklistItems: Yup.array().of(
        Yup.object({
          checklistId: Yup.number().required(),
          checklistComment: Yup.string().trim().nullable(),
          checklistAttachments: Yup.array(),
          checked: Yup.boolean(),
        })
      ),
    });
  }, [task, reportData]);

  const handleOpenAttachmentDialog = (target) => {
    setAttachmentDialogTarget(target);
    // open another dialog component here (not implemented)
  };
  const handleFilesSelected = (files, setFieldValue, values) => {
    if (!attachmentDialogTarget) return;

    if (attachmentDialogTarget.type === 'task') {
      const updated = [...(values.taskAttachments || []), ...files];
      setFieldValue('taskAttachments', updated);
    } else {
      const idx = attachmentDialogTarget.index;
      const updatedChecklist = [...values.checklistItems];
      const item = updatedChecklist[idx];
      updatedChecklist[idx] = {
        ...item,
        checklistAttachments: [...(item.checklistAttachments || []), ...files],
      };
      setFieldValue('checklistItems', updatedChecklist);
    }

    setAttachmentDialogTarget(null);
  };
  const handleSubmitReport = async (values, helpers) => {
    try {
      // Másolat készítése és mellékletek eltávolítása
      const { taskAttachments, checklistItems, ...rest } = values;
      const submissionData = {
        ...rest,
        checklistItems: (checklistItems || []).map(({ checklistAttachments, ...ciRest }) => ciRest),
        reportId,
      };

      const closedReportId = await serviceMonitorService.stopJob(submissionData);

      // ha a válasz nem szám, akkor legyen null
      const validReportId = typeof closedReportId === 'number' ? closedReportId : null;

      if (values.taskAttachments?.length) {
        await serviceCoreService.uploadFile(
          {
            serviceTaskId: selectedTaskId,
            serviceReportId: validReportId ?? 1,
            serviceTaskChecklistId: null,
          },
          values.taskAttachments
        );
      }

      for (const checklistItem of values.checklistItems) {
        if (checklistItem.checklistAttachments?.length) {
          await serviceCoreService.uploadFile(
            {
              serviceTaskId: selectedTaskId,
              serviceReportId: validReportId,
              serviceTaskChecklistId: checklistItem.checklistId,
            },
            checklistItem.checklistAttachments
          );
        }
      }

      onClose();
    } finally {
      helpers.setSubmitting(false);
      setRefetch(true);
    }
  };

  const handlePreview = (data) => {
    if (data instanceof File) {
      const url = URL.createObjectURL(data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (data.path) {
      const url = `${process.env.REACT_APP_API_URL}/${data.path}`;
      window.open(url, '_blank');
    }
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent={reportId ? 'Rögzítés' : 'Lejelentés'}
      fullWidth={true}
      maxWidth={maxWidth}
      contentStyle={{ bgcolor: 'grey.100', p: 1 }}
    >
      {!task && <div>Betöltés...</div>}
      {task && (
        <Formik
          enableReinitialize
          initialValues={reportData}
          validationSchema={validationSchema}
          onSubmit={handleSubmitReport}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Typography variant="h6" gutterBottom align="center" sx={{ my: 1 }}>
                  {task.service.workingNumber} - {task.serviceOperation.name}
                </Typography>
                {!reportId && (
                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                    <Box>
                      <Field
                        type="text"
                        name="taskDescription"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Megjegyzés" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.taskDescription} />}
                        InputLabelProps={{ shrink: true }}
                        multiline
                        rows={2}
                      />
                    </Box>
                    <Box mt={1}>
                      <Typography variant="subtitle2">Mellékletek {values.taskAttachmentRequired && '*'}</Typography>

                      {/* Korábbi mellékletek (task) */}
                      {(values.prevTaskAttachments || []).length > 0 && (
                        <Stack component="ul" sx={{ listStyle: 'none', pl: 0, mt: 1 }} spacing={0.5}>
                          {values.prevTaskAttachments.map((f, i) => (
                            <Box key={`prev-task-${i}`} component="li" display="flex" alignItems="center" gap={1}>
                              <Chip size="small" label="Korábbi" />
                              <Typography variant="caption">
                                {f.originalFileName || f.fileName || f.name || 'Fájl'}
                              </Typography>
                              <IconButton onClick={() => handlePreview(f)} size="small">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}

                      {/* Újonnan hozzáadott mellékletek (task) - törölhető */}
                      <Stack component="ul" sx={{ listStyle: 'none', pl: 0, mt: 1 }} spacing={0.5}>
                        {values.taskAttachments.map((f, i) => (
                          <Box key={i} component="li" display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption">{f.name || 'Fájl'}</Typography>
                            <IconButton onClick={() => handlePreview(f)} size="small">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              aria-label="Melléklet törlése"
                              size="small"
                              onClick={() =>
                                setFieldValue(
                                  'taskAttachments',
                                  values.taskAttachments.filter((_, idx) => idx !== i)
                                )
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenAttachmentDialog({ type: 'task' })}
                      >
                        Fájl hozzáadása
                      </Button>
                      {touched.taskAttachments && errors.taskAttachments && (
                        <Typography color="error" variant="caption">
                          {errors.taskAttachments}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )}
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>
                    Checklist
                  </Typography>
                  <Stack spacing={2}>
                    {values.checklistItems.length > 0 ? (
                      values.checklistItems.map((item, idx) => {
                        const itemErrors = errors.checklistItems && errors.checklistItems[idx];
                        const itemTouched = touched.checklistItems && touched.checklistItems[idx];
                        return (
                          <Paper key={item.checklistId || idx} variant="outlined" sx={{ p: 1 }}>
                            <Box>
                              <Field
                                name={`checklistItems[${idx}].checked`}
                                type="checkbox"
                                as={CheckboxUI}
                                fullWidth
                                label={<FormLabelUI text={item.name} />}
                              />
                            </Box>
                            <Box mt={1}>
                              <Field
                                type="text"
                                name={`checklistItems[${idx}].checklistComment`}
                                as={InputUI}
                                fullWidth
                                helperText={<FormErrorUI message={itemErrors?.checklistComment} />}
                                InputLabelProps={{ shrink: true }}
                                multiline
                                rows={2}
                                variant="standard"
                                label={<FormLabelUI text="Megjegyzés" />}
                              />
                            </Box>
                            <Box mt={1}>
                              <Typography variant="subtitle2">Mellékletek {item.attachmentRequired && '*'}</Typography>

                              {/* Korábbi mellékletek (checklist elem) */}
                              {(item.prevAttachments || []).length > 0 && (
                                <Stack component="ul" sx={{ listStyle: 'none', pl: 0, mt: 1 }} spacing={0.5}>
                                  {item.prevAttachments.map((f, fi) => (
                                    <Box
                                      key={`prev-ci-${item.checklistId}-${fi}`}
                                      component="li"
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Chip size="small" label="Korábbi" />
                                      <Typography variant="caption">
                                        {f.originalFileName || f.fileName || f.name || 'Fájl'}
                                      </Typography>
                                      <IconButton onClick={() => handlePreview(f)} size="small">
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                </Stack>
                              )}

                              {/* Újonnan hozzáadott mellékletek (checklist) - törölhető */}
                              <Stack component="ul" sx={{ listStyle: 'none', pl: 0, mt: 1 }} spacing={0.5}>
                                {(item.checklistAttachments || []).map((f, fi) => (
                                  <Box key={fi} component="li" display="flex" alignItems="center" gap={1}>
                                    <Typography variant="caption">{f.name || 'Fájl'}</Typography>
                                    <IconButton onClick={() => handlePreview(f)} size="small">
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      aria-label="Melléklet törlése"
                                      size="small"
                                      onClick={() => {
                                        const updatedChecklist = [...values.checklistItems];
                                        updatedChecklist[idx] = {
                                          ...item,
                                          checklistAttachments: (item.checklistAttachments || []).filter(
                                            (_, k) => k !== fi
                                          ),
                                        };
                                        setFieldValue('checklistItems', updatedChecklist);
                                      }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Stack>

                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenAttachmentDialog({ type: 'checklist', index: idx })}
                              >
                                Fájl hozzáadása
                              </Button>
                              {itemTouched?.checklistAttachments && itemErrors?.checklistAttachments && (
                                <FormErrorUI message={itemErrors?.checklistAttachments} />
                              )}
                            </Box>
                          </Paper>
                        );
                      })
                    ) : (
                      <Box align="center">
                        <Typography fontWeight="bold" fontStyle="italic">
                          Nincs checklist elem hozzáadva.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
                <Box mt={3} display="flex" gap={2} justifyContent="center" alignItems="center">
                  <ButtonUI
                    text="Rögzítés"
                    className="bg-success flex gap-2 items-center"
                    type="submit"
                    disabled={isSubmitting}
                  />
                  <ButtonUI
                    text="Mégse"
                    className="bg-primary flex items-center gap-2"
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                  />
                </Box>
              </Box>
              <FileUploadDialogUI
                open={Boolean(attachmentDialogTarget)}
                handleClose={() => setAttachmentDialogTarget(null)}
                handleUpload={(files) => {
                  //handleUpload(files);
                  handleFilesSelected(files, setFieldValue, values);
                  setAttachmentDialogTarget(null);
                }}
              />
            </Form>
          )}
        </Formik>
      )}
      {/* Attachment dialog stub: implement real dialog & file input elsewhere */}
    </DialogUI>
  );
};

export default ServiceMonitorReportDialog;
